import moment from 'moment';
import React from 'react';

import CustomLineItemsStep from './CustomLineItemsStep';
import ExtraLineItemsStep from './ExtraLineItemsStep';
import VoucherStep from './VoucherStep';

export const getCategoryLabel = (categories, key, intl) => {
  const cat = categories.find((c) => c.key === key);
  return cat ? intl.formatMessage({ id: cat.label }) : key;
};

export const diffTodayAndDate = (date) => {
  const todayMonth = moment().month(); // September would be 8
  const todayYear = moment().year();
  const targetMonth = moment(date).month(); // January would be 0
  const targetYear = moment(date).year();

  // Calculate the difference in years and months
  let yearDifference = targetYear - todayYear;
  let monthDifference = targetMonth - todayMonth;

  // Adjust for negative month difference
  if (monthDifference < 0) {
    yearDifference -= 1;
    monthDifference += 12;
  }

  // Return total difference in months
  return yearDifference * 12 + monthDifference;
};

export const formatStepLabel = (label, selectedCount, intl) => {
  if (selectedCount > 0) {
    const countText = intl.formatMessage(
      { id: 'BookingDatesForm.selectedCount' },
      { count: selectedCount },
    );
    return `${label} (${countText})`;
  }
  return label;
};

const getListingLineItems = (currentListing, key) =>
  currentListing?.attributes?.publicData?.[key] || {};

export const createSteps = ({
  appliedVoucher,
  control,
  currentListing,
  disabled,
  extraLineItemsSelectedCount,
  customLineItemsSelectedCount,
  intl,
  onApplyVoucher,
  voucherValidationError,
  voucherValidationInProgress,
}) => {
  const extraLineItems = getListingLineItems(currentListing, 'extraLineItems');
  const customLineItems = getListingLineItems(currentListing, 'customLineItems');

  const extraLineItemsLabel = intl.formatMessage({ id: 'BookingDatesForm.extraLineItemsStepLabel' });
  const customLineItemsLabel = intl.formatMessage({ id: 'BookingDatesForm.customLineItemsStepLabel' });
  const voucherStepLabel = intl.formatMessage({ id: 'BookingDatesForm.voucherStepLabel' });

  return [
    ...(Object.keys(extraLineItems).length > 0 ? [{
      label: formatStepLabel(extraLineItemsLabel, extraLineItemsSelectedCount, intl),
      component: (
        <ExtraLineItemsStep
          control={control}
          disabled={disabled}
          extraLineItems={extraLineItems}
        />
      ),
    }] : []),
    ...(Object.keys(customLineItems).length > 0 ? [{
      label: formatStepLabel(customLineItemsLabel, customLineItemsSelectedCount, intl),
      component: (
        <CustomLineItemsStep
          control={control}
          customLineItems={customLineItems}
          disabled={disabled}
        />
      ),
    }] : []),
    {
      label: voucherStepLabel,
      component: (
        <VoucherStep
          appliedVoucher={appliedVoucher}
          onApplyVoucher={onApplyVoucher}
          voucherValidationError={voucherValidationError}
          voucherValidationInProgress={voucherValidationInProgress}
        />
      ),
    },
  ];
};

export const createGuestCount = (guestOptions = null, isCampingGround, intl) => {
  const maxPets = guestOptions?.maxPetCount || 0;

  return [
    {
      name: 'adult',
      label: intl.formatMessage({ id: 'BookingDatesForm.adultsLabel' }),
      infoLabel: intl.formatMessage({ id: 'BookingDatesForm.adultsInfoLabel' }),
      minimum: 1,
    },
    {
      name: 'child',
      label: intl.formatMessage({ id: 'BookingDatesForm.childrenLabel' }),
      infoLabel: intl.formatMessage({ id: 'BookingDatesForm.childrenInfoLabel' }),
      minimum: 0,
    },
    {
      name: 'toddler',
      label: intl.formatMessage({ id: 'BookingDatesForm.toddlersLabel' }),
      infoLabel: intl.formatMessage({ id: 'BookingDatesForm.toddlersInfoLabel' }),
      minimum: 0,
    },
    ...(isCampingGround ? [{
      name: 'pet',
      label: intl.formatMessage({ id: 'BookingDatesForm.petsLabel' }),
      minimum: 0,
      maximum: maxPets,
      excludeFromTotal: true,
    }] : []),
  ];
};

export const createCurrentGuestCountLabels = (intl) => ({
  adult: (count) => intl.formatMessage({ id: 'BookingDatesForm.adult.countLabel' }, { count }),
  child: (count) => intl.formatMessage({ id: 'BookingDatesForm.child.countLabel' }, { count }),
  toddler: (count) => intl.formatMessage({ id: 'BookingDatesForm.toddler.countLabel' }, { count }),
  pet: (count) => intl.formatMessage({ id: 'BookingDatesForm.pet.countLabel' }, { count }),
});
